import { useEffect, useState } from "react";
import { api, enviarMensajeAhora } from "../api.ts";
import { fechaLarga, horaLarga } from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";

interface Sugerencia {
  pacienteId: string; hc: number; nombre: string; celular: string; motivo: "cancelo" | "pendiente" | "control"; nombreMotivo: string; detalle: string; meses: number;
  hueco: { profesionalId: string; profesional: string; fecha: string; hora: string; duracion: number }; texto: string;
}
interface Datos { dias: number; duracion: number; whatsappBusiness: boolean; huecosLibres: number; sinHueco: number; sugerencias: Sugerencia[] }

const MOTIVO_CITA: Record<Sugerencia["motivo"], string> = { cancelo: "Consulta", pendiente: "Tratamiento pendiente", control: "Control y limpieza" };

/**
 * Llenar la agenda: Donna junta los huecos libres con los pacientes a quienes tiene sentido escribirles y deja el mensaje redactado.
 * Solo sugiere: recepción lee, corrige si quiere y toca «Enviar por WhatsApp». Cuando el paciente responde que sí, se agenda con un clic.
 */
export function LlenarAgenda({ ir }: { ir: (r: string) => void }) {
  const [dias, setDias] = useState(7);
  const [duracion, setDuracion] = useState(30);
  const [d, setD] = useState<Datos | null>(null);
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [hechos, setHechos] = useState<Record<string, "enviado" | "agendada">>({});
  const [aviso, setAviso] = useState("");
  const a = useAccion();

  useEffect(() => {
    setHechos({}); setTextos({});
    api<Datos>("GET", `/api/agenda/llenar?dias=${dias}&duracion=${duracion}`).then((r) => { setD(r); a.setError(null); }).catch(a.setError);
  }, [dias, duracion]);

  const texto = (s: Sugerencia) => textos[s.pacienteId] ?? s.texto;
  async function enviar(s: Sugerencia) {
    const r = await a.correr(() => enviarMensajeAhora("/api/mensajes/manual", { pacienteId: s.pacienteId, texto: texto(s), canal: "whatsapp" }));
    if (!r) return;
    setHechos((h) => ({ ...h, [s.pacienteId]: "enviado" }));
    setAviso(r.enviado ? `Mensaje enviado a ${s.nombre} por WhatsApp.` : `Se abrió WhatsApp con el mensaje para ${s.nombre}: solo toca Enviar.`);
  }
  async function agendar(s: Sugerencia) {
    const h = s.hueco;
    const r = await a.correr(() => api("POST", "/api/citas", { pacienteId: s.pacienteId, profesionalId: h.profesionalId, fecha: h.fecha, hora: h.hora, duracion: h.duracion, motivo: s.motivo === "cancelo" && s.detalle ? s.detalle : MOTIVO_CITA[s.motivo] }));
    if (!r) return;
    setHechos((x) => ({ ...x, [s.pacienteId]: "agendada" }));
    setAviso(`Cita agendada para ${s.nombre}: ${fechaLarga(h.fecha)} a las ${horaLarga(h.hora)}.`);
  }

  const detalle = (s: Sugerencia) => s.motivo === "pendiente" ? `Pendiente: ${s.detalle}` : s.motivo === "control" ? `Hace ${s.meses} meses que no viene` : s.detalle ? `Cita: ${s.detalle}` : "No pudo venir a su última cita";
  return (
    <Seccion titulo="Llenar la agenda" acciones={
      <span className="fila" style={{ gap: 6 }}>
        <select className="entrada" style={{ width: "auto" }} value={dias} onChange={(e) => setDias(Number(e.target.value))} aria-label="Días a revisar">
          <option value={3}>Próximos 3 días</option><option value={7}>Próximos 7 días</option><option value={14}>Próximos 14 días</option>
        </select>
        <select className="entrada" style={{ width: "auto" }} value={duracion} onChange={(e) => setDuracion(Number(e.target.value))} aria-label="Duración de la cita">
          <option value={30}>Citas de 30 min</option><option value={45}>Citas de 45 min</option><option value={60}>Citas de 1 hora</option>
        </select>
      </span>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Donna solo sugiere: nada se envía hasta que tocas «Enviar por WhatsApp». {d?.whatsappBusiness ? "Sale por WhatsApp Business." : "Se abre WhatsApp con el mensaje listo y solo tocas Enviar."} Quien ya tiene cita, recibió un mensaje en las últimas dos semanas o pidió no recibir promociones no aparece.
      </p>
      {d && <p style={{ margin: 0 }}><b>{d.huecosLibres}</b> hueco{d.huecosLibres === 1 ? "" : "s"} libre{d.huecosLibres === 1 ? "" : "s"} en los próximos {d.dias} días · <b>{d.sugerencias.length}</b> paciente{d.sugerencias.length === 1 ? "" : "s"} sugerido{d.sugerencias.length === 1 ? "" : "s"}{d.sinHueco ? ` (${d.sinHueco} más esperan que se libere un hueco)` : ""}.</p>}
      {aviso && <div className="aviso ok" role="status">{aviso}</div>}
      <AvisoError error={a.error} />
      {d && !d.sugerencias.length && <p className="muted" style={{ margin: 0 }}>{d.huecosLibres ? "No hay pacientes para sugerir en este momento." : "No quedan huecos libres en este período."}</p>}
      <ul className="lista-abonos">
        {d?.sugerencias.map((s) => {
          const hecho = hechos[s.pacienteId];
          return (
            <li key={s.pacienteId} className="cumple">
              <div className="fila" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap" }}>
                <span><button className="enlace" onClick={() => ir(`paciente/${s.pacienteId}`)}><b>{s.nombre}</b></button> <span className="muted mono">HC {s.hc}</span> <span className="chip">{s.nombreMotivo}</span></span>
                {hecho === "agendada" ? <span className="chip azul">Cita agendada</span> : hecho === "enviado" ? <span className="chip azul">Mensaje enviado</span> : null}
              </div>
              <span className="muted" style={{ fontSize: 13 }}>{detalle(s)} · <span className="mono">{s.celular}</span></span>
              <span style={{ fontSize: 14 }}>Hueco sugerido: <b>{fechaLarga(s.hueco.fecha)}, {horaLarga(s.hueco.hora)}</b>{s.hueco.profesional ? ` con ${s.hueco.profesional}` : ""} ({s.hueco.duracion} min)</span>
              {!hecho && (
                <>
                  <textarea className="entrada" rows={4} value={texto(s)} maxLength={1000} onChange={(e) => setTextos({ ...textos, [s.pacienteId]: e.target.value })} aria-label={`Mensaje para ${s.nombre}`} />
                  <div className="fila" style={{ flexWrap: "wrap" }}>
                    <button className="btn chico wa" disabled={a.ocupado || !texto(s).trim()} onClick={() => enviar(s)}>Enviar por WhatsApp</button>
                    <button className="btn chico" disabled={a.ocupado} onClick={() => agendar(s)} title="Úsalo cuando el paciente respondió que sí: crea la cita en ese hueco">Agendar este horario</button>
                  </div>
                </>
              )}
              {hecho === "enviado" && <div className="fila"><button className="btn chico" disabled={a.ocupado} onClick={() => agendar(s)} title="Cuando el paciente responda que sí">Agendar este horario</button></div>}
            </li>
          );
        })}
      </ul>
    </Seccion>
  );
}
